import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

/**
 * Calls `onOutside` when a pointer press lands outside `ref`, while `active`.
 *
 * Uses `pointerdown` in the capture phase — it fires on any press (mouse or touch),
 * including on non-focusable areas that never emit a blur. That's why this is more
 * reliable than an onBlur-based dismiss: clicking empty page space or a plain table
 * cell closes the popover, not only clicking a focusable element or the trigger.
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onOutside: () => void,
  active = true,
): void {
  // Keep the latest callback in a ref so the listener isn't re-subscribed every render.
  const cb = useRef(onOutside)
  useEffect(() => {
    cb.current = onOutside
  })
  useEffect(() => {
    if (!active) return
    function handle(e: PointerEvent) {
      const el = ref.current
      if (el && !el.contains(e.target as Node | null)) cb.current()
    }
    document.addEventListener('pointerdown', handle, true)
    return () => document.removeEventListener('pointerdown', handle, true)
  }, [ref, active])
}
